import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'react-md/lib/Buttons';
import { FontIcon } from 'react-md/lib/FontIcons';
import { DialogContainer } from 'react-md/lib/Dialogs';
import { toast } from 'react-toastify';
import moment from 'moment-timezone';
import LoadingButton from 'Components/Common/LoadingButton';
import Loading from 'Components/Common/Loading';
import {
   IdentityActions,
   IdentityTypes,
   selectTrustedDevices,
   selectRevokeDeviceResponse,
} from 'State/Redux/IdentityRedux';
import {
   selectIsLoading,
   selectIsSuccess,
   selectIsError,
} from 'State/Redux/AsyncRedux';
import { FN_EMPTY_VOID } from 'Util/Helpers/Empty';
import MfaTrustedDevice from 'Models/Identity/MfaTrustedDevice';
import './TrustedDevices.scss';

const TIMESTAMP_FORMAT = 'MMM D, YYYY h:mm A';

const isExpired = (device: MfaTrustedDevice): boolean =>
   moment(device.expiresAt).isBefore(moment());

const formatTimestamp = (value: string): string =>
   moment(value).format(TIMESTAMP_FORMAT);

const TrustedDevices = (): JSX.Element => {
   const dispatch = useDispatch();
   const trustedDevices = useSelector(selectTrustedDevices);
   const revokeDeviceResponse = useSelector(selectRevokeDeviceResponse);
   const isLoadingDevices = useSelector(
      selectIsLoading([IdentityTypes.GET_TRUSTED_DEVICES_REQUEST])
   );
   const isDevicesError = useSelector(
      selectIsError([IdentityTypes.GET_TRUSTED_DEVICES_REQUEST])
   );
   const isRevoking = useSelector(
      selectIsLoading([IdentityTypes.REVOKE_DEVICE_REQUEST])
   );
   const isRevokeSuccess = useSelector(
      selectIsSuccess([IdentityTypes.REVOKE_DEVICE_REQUEST])
   );

   const [revokingId, setRevokingId] = useState<string | null>(null);
   const [confirmDeviceId, setConfirmDeviceId] = useState<string | null>(null);
   const wasRevoking = useRef(false);

   const refreshDevices = useCallback((): void => {
      dispatch(IdentityActions.getTrustedDevicesRequest());
   }, [dispatch]);

   useEffect(refreshDevices, [refreshDevices]);

   const devices = (trustedDevices ?? []).filter((d): boolean => !d.revokedAt);

   const closeDialog = (): void => {
      setConfirmDeviceId(null);
   };

   const handleConfirm = (): void => {
      if (confirmDeviceId) {
         setRevokingId(confirmDeviceId);
         dispatch(IdentityActions.revokeDeviceRequest(confirmDeviceId));
      }
      closeDialog();
   };

   useEffect((): void => {
      if (wasRevoking.current && !isRevoking) {
         if (isRevokeSuccess) {
            toast.success('Device access has been revoked.');
            refreshDevices();
         } else {
            toast.error(
               revokeDeviceResponse?.message || 'Unable to revoke device.'
            );
         }
         setRevokingId(null);
         dispatch(IdentityActions.revokeDeviceResponseReset());
      }
      wasRevoking.current = isRevoking;
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isRevoking]);

   const confirmDevice = devices.find((d): boolean => d.id === confirmDeviceId);

   return (
      <Loading
         isLoading={isLoadingDevices && devices.length === 0}
         isError={isDevicesError}
         tryReload={refreshDevices}
      >
         <div className="md-grid trusted-devices-page">
            <div className="md-cell md-cell--12 trusted-devices-header">
               <h2 className="trusted-devices-title">Trusted devices</h2>
               <p className="trusted-devices-subtitle">
                  Browsers you&apos;ve chosen to skip the verification code on,
                  for a limited time.
               </p>
            </div>

            {devices.length === 0 && !isLoadingDevices && (
               <div className="md-cell md-cell--12 trusted-devices-empty">
                  No trusted devices found for your account.
               </div>
            )}

            {devices.map(
               (device): JSX.Element => {
                  const expired = isExpired(device);

                  return (
                     <div
                        key={device.id}
                        className="md-cell md-cell--12 trusted-devices-card-wrapper"
                     >
                        <div className="trusted-devices-card">
                           <div
                              className={`trusted-devices-card-icon${
                                 expired
                                    ? ' trusted-devices-card-icon--expired'
                                    : ''
                              }`}
                           >
                              <FontIcon>devices</FontIcon>
                           </div>
                           <div className="trusted-devices-card-body">
                              <div className="trusted-devices-card-title-row">
                                 <strong>
                                    {device.deviceLabel || 'Unknown device'}
                                 </strong>
                                 {expired && (
                                    <span className="trusted-devices-chip trusted-devices-chip--expired">
                                       EXPIRED
                                    </span>
                                 )}
                              </div>
                              <div className="trusted-devices-meta">
                                 Created:&nbsp;
                                 {formatTimestamp(device.createdAt)}
                                 {' | '}
                                 Last used:&nbsp;
                                 {moment(
                                    device.lastUsedAt
                                       ? device.lastUsedAt
                                       : device.createdAt
                                 ).fromNow()}
                              </div>
                              <div className="trusted-devices-meta">
                                 {expired ? (
                                    <>
                                       Expired{' '}
                                       <strong className="trusted-devices-expired-date">
                                          {formatTimestamp(device.expiresAt)}
                                       </strong>
                                    </>
                                 ) : (
                                    <>
                                       Trusted until{' '}
                                       <strong>
                                          {formatTimestamp(device.expiresAt)}
                                       </strong>
                                    </>
                                 )}
                              </div>
                              <div className="trusted-devices-fingerprint">
                                 {device.createdFromIp || 'Unknown IP'}
                              </div>
                           </div>
                           <div className="trusted-devices-card-action">
                              <LoadingButton
                                 raised
                                 className="trusted-devices-action-btn"
                                 isLoading={revokingId === device.id}
                                 onClick={(): void =>
                                    setConfirmDeviceId(device.id)
                                 }
                              >
                                 <FontIcon
                                    style={{
                                       fontSize: '16px',
                                       marginRight: '6px',
                                       color: '#fff',
                                    }}
                                 >
                                    delete
                                 </FontIcon>
                                 {expired ? 'Remove' : 'Revoke'}
                              </LoadingButton>
                           </div>
                        </div>
                     </div>
                  );
               }
            )}

            <DialogContainer
               id="trusted-devices-confirm-dialog"
               visible={confirmDeviceId !== null}
               title="Revoke Device"
               width={500}
               portal
               onHide={FN_EMPTY_VOID}
               contentStyle={{ padding: '0 24px 24px' }}
               footerStyle={{ padding: '0 24px 24px 24px' }}
               actions={[
                  <Button key="cancel" flat onClick={closeDialog}>
                     Cancel
                  </Button>,
                  <Button
                     key="confirm"
                     flat
                     className="trusted-devices-dialog-confirm-btn"
                     onClick={handleConfirm}
                  >
                     Confirm
                  </Button>,
               ]}
            >
               <span>
                  Are you sure you want to revoke access for
                  {confirmDevice
                     ? ` ${confirmDevice.deviceLabel || 'this device'}`
                     : ' this device'}
                  ? It will be required to verify again at next sign-in.
               </span>
            </DialogContainer>
         </div>
      </Loading>
   );
};

export default TrustedDevices;
